import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { X, Scaling, Loader2, Download, Scissors, Sparkles, Lock, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { HudButton } from '../hud';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };

export const upscaleUrl = (projectId, jobId, variant = 'full', download = false) =>
  `${API}/projects/${projectId}/generations/${jobId}/upscaled?variant=${variant}${download ? '&download=1' : ''}`;

const MODES = [
  { id: 'deterministic', icon: Lock, label: 'Déterministe', sub: '100 % fidèle · instantané · aucune dérive', hint: 'Lanczos + netteté adaptative. Le rendu est simplement plus net ; rien n\'est inventé.' },
  { id: 'ai', icon: Sparkles, label: 'Affiner par IA', sub: 'Nano Banana 4K natif · micro-détails', hint: 'L\'IA redessine la même image en 4K avec plus de grain de matière. Une mesure de similarité signale toute dérive (< 80 %).' },
];

/**
 * UpscaleDialog — V15.29 : « Agrandir en 4K » d'un rendu validé (+ détourage PNG).
 * Le job tourne en fond ; l'état `item.upscale` est rafraîchi par le polling de la galerie,
 * complété ici par un poll dédié tant que le statut est « processing ».
 */
const UpscaleDialog = ({ projectId, item, onClose, onChanged }) => {
  const [mode, setMode] = useState(item?.upscale?.mode || 'deterministic');
  const [cutout, setCutout] = useState(item?.upscale?.cutout_requested ?? true);
  const [state, setState] = useState(item?.upscale || null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (state?.status !== 'processing') return undefined;
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/projects/${projectId}/generations/${item.job_id}/upscale`, opts);
        const st = r.data?.upscale || null;
        setState(st);
        if (st && st.status !== 'processing') {
          onChanged?.(item.job_id, st);
          if (st.status === 'completed') toast.success(`Livrable 4K prêt · ${st.width}×${st.height}`);
          else toast.error(st.error || "Échec de l'agrandissement.");
        }
      } catch { /* silencieux */ }
    }, 3000);
    return () => clearInterval(t);
  }, [state?.status, projectId, item.job_id, onChanged]);

  const launch = async () => {
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/projects/${projectId}/generations/${item.job_id}/upscale`, { mode, cutout }, opts);
      setState(r.data?.upscale || { status: 'processing', mode });
      onChanged?.(item.job_id, r.data?.upscale);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de lancer l'agrandissement.");
    } finally {
      setSubmitting(false);
    }
  };

  const done = state?.status === 'completed';
  const processing = state?.status === 'processing';

  return createPortal(
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" data-testid="upscale-dialog"
         style={{ background: 'rgba(6,8,12,0.86)', backdropFilter: 'blur(14px)' }} onClick={onClose} role="dialog" aria-modal="true">
      <div className="relative w-full max-w-xl rounded-2xl overflow-hidden"
           style={{ background: 'var(--c-surface-1)', border: '1px solid var(--c-border)', boxShadow: '0 30px 80px -20px rgba(0,0,0,0.85)', maxHeight: '92vh' }}
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 pb-3" style={{ borderBottom: '1px solid var(--c-border)' }}>
          <div>
            <span className="hud-eyebrow hud-eyebrow-gold">Livrable client</span>
            <h3 className="mt-1 font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 20 }}>
              Agrandir en 4K
            </h3>
            <p className="text-[12.5px] mt-1.5" style={{ color: 'var(--c-text-2)' }}>
              {item.lot_number || item.job_id} — le petit côté atteint 2160 px (1920×1080 toujours couvert, format d'origine conservé).
            </p>
          </div>
          <button type="button" onClick={onClose} data-testid="upscale-dialog-close" className="rounded-lg p-2" style={{ color: 'var(--c-text-muted)' }}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto" style={{ maxHeight: 'calc(92vh - 150px)' }}>
          <div className="grid grid-cols-2 gap-2" data-testid="upscale-mode-picker">
            {MODES.map((m) => {
              const on = mode === m.id;
              return (
                <button key={m.id} type="button" onClick={() => setMode(m.id)} disabled={processing}
                        data-testid={`upscale-mode-${m.id}`}
                        className="text-left rounded-xl p-3 transition-all disabled:opacity-50"
                        style={{ background: on ? 'rgba(217,160,91,0.12)' : 'var(--c-surface-2)', border: `1px solid ${on ? 'var(--c-border-accent)' : 'var(--c-border)'}` }}>
                  <div className="flex items-center gap-1.5 text-[12.5px] font-semibold" style={{ color: on ? 'var(--c-gold)' : 'var(--c-text)' }}>
                    <m.icon className="w-3.5 h-3.5" /> {m.label}
                  </div>
                  <div className="text-[10.5px] mt-0.5" style={{ color: 'var(--c-text-2)' }}>{m.sub}</div>
                  <div className="text-[10px] mt-1.5 leading-snug" style={{ color: 'var(--c-text-faint)' }}>{m.hint}</div>
                </button>
              );
            })}
          </div>

          <label className="flex items-start gap-2.5 rounded-xl p-3 cursor-pointer" style={{ background: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}>
            <input type="checkbox" checked={cutout} onChange={(e) => setCutout(e.target.checked)} disabled={processing} className="mt-0.5" data-testid="upscale-cutout-checkbox" />
            <span>
              <span className="flex items-center gap-1.5 text-[12.5px] font-semibold" style={{ color: 'var(--c-text)' }}><Scissors className="w-3.5 h-3.5" /> Détourage automatique (PNG transparent)</span>
              <span className="block text-[10.5px] mt-0.5 leading-snug" style={{ color: 'var(--c-text-faint)' }}>
                Fond uniforme détecté (blanc, gris, studio…) → fond retiré. Fond complexe (ciel, abords) → signalé ; le mode IA tente alors un détourage sur fond neutre.
              </span>
            </span>
          </label>

          {state && (
            <div className="rounded-xl p-3 space-y-2" data-testid="upscale-status"
                 style={{ background: 'var(--c-surface-2)', border: `1px solid ${done ? 'rgba(34,197,94,0.35)' : state.status === 'failed' ? 'rgba(239,68,68,0.4)' : 'var(--c-border)'}` }}>
              <div className="flex items-center gap-2 text-[12px] font-semibold" style={{ color: done ? '#86EFAC' : state.status === 'failed' ? '#FCA5A5' : 'var(--c-text)' }}>
                {processing && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {done && <CheckCircle2 className="w-3.5 h-3.5" />}
                {processing ? `Agrandissement ${state.mode === 'ai' ? 'IA' : 'déterministe'} en cours…`
                  : done ? `4K prêt · ${state.width}×${state.height} · ${state.engine === 'nano_banana_4k' ? 'IA' : 'déterministe'}${typeof state.similarity === 'number' ? ` · similarité ${state.similarity} %` : ''}`
                  : `Échec : ${state.error || 'erreur inconnue'}`}
              </div>
              {(state.warnings || []).map((w, i) => (
                <div key={i} className="flex items-start gap-1.5 text-[10.5px] leading-snug" style={{ color: '#FCD34D' }} data-testid="upscale-warning">
                  <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" /><span>{w}</span>
                </div>
              ))}
              {done && (
                <div className="flex flex-wrap gap-2 pt-1">
                  <a href={upscaleUrl(projectId, item.job_id, 'full', true)} data-testid="upscale-download-full"
                     className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11.5px] font-semibold"
                     style={{ background: 'var(--c-gold)', color: '#0F1115' }}>
                    <Download className="w-3.5 h-3.5" /> Télécharger le 4K
                  </a>
                  {state.cutout_path && (
                    <a href={upscaleUrl(projectId, item.job_id, 'cutout', true)} data-testid="upscale-download-cutout"
                       className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11.5px] font-semibold"
                       style={{ background: 'var(--c-surface-3)', color: 'var(--c-text)', border: '1px solid var(--c-border)' }}>
                      <Scissors className="w-3.5 h-3.5" /> PNG détouré
                    </a>
                  )}
                  <a href={upscaleUrl(projectId, item.job_id, state.cutout_path ? 'cutout' : 'full')} target="_blank" rel="noreferrer"
                     data-testid="upscale-open-full" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11.5px]"
                     style={{ color: 'var(--c-text-2)', border: '1px solid var(--c-border)' }}>
                    Ouvrir
                  </a>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4" style={{ borderTop: '1px solid var(--c-border)' }}>
          <HudButton variant="ghost" size="sm" onClick={onClose} data-testid="upscale-cancel">Fermer</HudButton>
          <HudButton size="sm" onClick={launch} disabled={submitting || processing} leftIcon={processing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Scaling className="w-3.5 h-3.5" />} data-testid="upscale-launch-btn">
            {done ? 'Relancer' : processing ? 'En cours…' : 'Agrandir en 4K'}
          </HudButton>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default UpscaleDialog;
